import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box, IconButton, Tooltip, Menu, MenuItem, Checkbox, FormControlLabel,
  Dialog, DialogContent, DialogActions, Button, Snackbar, Alert,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  alterarExistirCobrancaJudicialDoProcessoService,
  findInformacoesAdicionaisInpescaoProcessoService,
} from '../../../../services/core/processos/processosService';
import * as C from '../../../theme/colors.constants';

/**
 * Espelha `SinalizacaoCores.jsx` (tela real de Inspecionar Processo, AW2-911).
 * Cores amostradas de `InspecionarProcessoStyles.jsx` (`colorsSinalizacaoBoxCores`).
 */
const MARCADORES = [
  {
    id: 'cobrancaJudicial',
    cor: '#ff8ac1',
    tooltip: 'Cobrança Judicial',
    label: 'Cobrança Judicial',
    editavel: true,
  },
  {
    id: 'devedorClienteAtivo',
    cor: '#93ecf1',
    tooltip: 'O Devedor do Processo é também um Cliente ativo',
    label: 'Devedor é um Cliente ativo',
    editavel: false,
  },
  {
    id: 'titulosAntigos',
    cor: '#fafd79',
    tooltip: 'Processo com Títulos antigos',
    label: 'Processo com Títulos antigos',
    editavel: false,
  },
  {
    id: 'honorariosJurosZero',
    cor: '#bcf5a2',
    tooltip: 'O percentual de recebimento de Honorários e de Juros é igual a zero',
    label: 'Isenção de comissionamento',
    editavel: false,
  },
];

const MENSAGEM_ERRO_COBRANCA_JUDICIAL = 'Não foi possível alterar a Cobrança Judicial.';

/** Portada 1:1 de `SinalizacaoCores.jsx` (habilitaCobrancaJudicial). */
function habilitaCobrancaJudicial(permitirAjuizarProcesso, podeAlterarCobrancaJudicial, podeModificarFase) {
  if (permitirAjuizarProcesso === 'N' && podeAlterarCobrancaJudicial && podeModificarFase) {
    return true;
  }
  if (permitirAjuizarProcesso === 'S' && podeModificarFase) {
    return true;
  }
  if (permitirAjuizarProcesso === 'N' && !podeAlterarCobrancaJudicial) {
    return false;
  }
  return podeModificarFase;
}

function MarcadoresCoresProcesso({
  idProcesso,
  sinalizacaoCoresQuery,
  permitirAjuizarProcesso,
  podeModificarFase,
  setSinalizacaoInfo,
}) {
  const [anchor, setAnchor] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const [erro, setErro] = useState('');

  const permissoesUsuario = useSelector(state => state.authentication.userInformation.permissoes);
  const podeAlterarCobrancaJudicial = Boolean(permissoesUsuario?.includes('ROLE_1209'));

  const cobrancaJudicialHabilitada = habilitaCobrancaJudicial(
    permitirAjuizarProcesso,
    podeAlterarCobrancaJudicial,
    podeModificarFase,
  );

  async function aplicarCobrancaJudicial(checked) {
    if (!idProcesso) {
      return;
    }

    try {
      setSalvando(true);
      await alterarExistirCobrancaJudicialDoProcessoService(idProcesso, checked);
      const { data } = await findInformacoesAdicionaisInpescaoProcessoService(idProcesso);
      setSinalizacaoInfo(data);
    } catch {
      setErro(MENSAGEM_ERRO_COBRANCA_JUDICIAL);
    } finally {
      setSalvando(false);
    }
  }

  function onChangeCobrancaJudicial(event) {
    const { checked } = event.target;

    if (checked && permitirAjuizarProcesso === 'N') {
      setConfirmacaoAberta(true);
      return;
    }

    aplicarCobrancaJudicial(checked);
  }

  function onConfirmarCobrancaJudicial() {
    setConfirmacaoAberta(false);
    aplicarCobrancaJudicial(true);
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {MARCADORES.map((marcador) => {
          const ativo = Boolean(sinalizacaoCoresQuery?.[marcador.id]);
          return (
            <Tooltip key={marcador.id} title={ativo ? marcador.tooltip : ''} arrow>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '5px',
                  mx: 0.25,
                  border: `1px solid ${ativo ? marcador.cor : C.borderGrey}`,
                  backgroundColor: ativo ? marcador.cor : 'transparent',
                }}
              />
            </Tooltip>
          );
        })}
        <Tooltip title="Sinalizadores" arrow>
          <IconButton
            size="small"
            aria-label="Sinalizadores"
            onClick={e => setAnchor(e.currentTarget)}
            sx={{ color: C.navy, ml: -0.5 }}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {MARCADORES.map(marcador => (
          <MenuItem key={marcador.id}>
            <FormControlLabel
              label={marcador.label}
              control={(
                <Checkbox
                  checked={Boolean(sinalizacaoCoresQuery?.[marcador.id])}
                  disabled={marcador.editavel ? !cobrancaJudicialHabilitada || salvando : true}
                  onChange={marcador.editavel ? onChangeCobrancaJudicial : undefined}
                />
              )}
            />
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={confirmacaoAberta} onClose={() => setConfirmacaoAberta(false)}>
        <DialogContent sx={{ fontSize: 16, textAlign: 'justify' }}>
          No cadastro deste Cliente está configurado para não permitir ajuizar processos.
          <br />
          Confirma a marcação de &ldquo;Cobrança Judicial&rdquo; para este processo?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmacaoAberta(false)}>Cancelar</Button>
          <Button variant="contained" onClick={onConfirmarCobrancaJudicial}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

MarcadoresCoresProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sinalizacaoCoresQuery: PropTypes.shape({
    cobrancaJudicial: PropTypes.bool,
    devedorClienteAtivo: PropTypes.bool,
    titulosAntigos: PropTypes.bool,
    honorariosJurosZero: PropTypes.bool,
  }),
  permitirAjuizarProcesso: PropTypes.oneOf(['S', 'N']),
  podeModificarFase: PropTypes.bool,
  setSinalizacaoInfo: PropTypes.func.isRequired,
};

MarcadoresCoresProcesso.defaultProps = {
  idProcesso: null,
  sinalizacaoCoresQuery: null,
  permitirAjuizarProcesso: null,
  podeModificarFase: false,
};

export default React.memo(MarcadoresCoresProcesso);
